import { Component } from '@theme/component'
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events'
import { morph } from '@theme/morph'

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl

  /** @type {AbortController | undefined} */
  #abortController

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener('change', this.variantChanged.bind(this))
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  async variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return

    this.updateSelectedOption(event.target)
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }))

    // Validate the new variant combination
    const selectedOptions = this.selectedOptionsValues
    const bestMatchingVariant = this.validateVariantCombination(selectedOptions, event)

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog')

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const newUrl = event.target.dataset.connectedProductUrl
    const loadsNewProduct = isOnProductPage
    await this.fetchUpdatedSection(this.buildRequestUrl(event.target, bestMatchingVariant), loadsNewProduct)

    const url = new URL(window.location.href)

    let variantId

    if (bestMatchingVariant && !bestMatchingVariant.isValid) {
      variantId = bestMatchingVariant.variant.id
    } else if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex]
      variantId = selectedOption?.dataset.variantId || null
    }

    if (!bestMatchingVariant.isValid) {
      this.updateSelectors(bestMatchingVariant.variant)
    }

    this.updateWishlistVariant(variantId)

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId)
      } else {
        url.searchParams.delete('variant')
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct && newUrl) {
      url.pathname = newUrl
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString())
    }

    document.querySelectorAll('product-nutrition').forEach(nutrition => {
      nutrition.disconnectedCallback()
      nutrition.connectedCallback()
    })
  }


  /**
   * Updates the selectors to the selected variant.
   * 
   * @param {any} variant - The selected variant.
   */
  updateSelectors(variant) {
    console.log('variant', variant)

    this.querySelectorAll('select').forEach((select) => {
      for (const option of select.options) {
        if (variant.options.some((/** @type {any} */ selectedOption) => selectedOption.id === Number(option.dataset.optionValueId))) {
          option.setAttribute('selected', 'selected')
        } else {
          option.removeAttribute('selected')
        }
      }
    })

  }

  /**
   * Validates if the selected combination of options represents a valid variant.
   * @param {string[]} selectedOptionsValues - The currently selected option values.
   * @param {Event | null} [event] - The variant change event.
   * @returns {{isValid: boolean, variant: any|null}} Object containing validation result and best matching variant if invalid.
   */
  validateVariantCombination(selectedOptionsValues, event = null) {
    const jsonScript = this.querySelector('script[type="application/json"]#available-variants')
    if (!jsonScript || !jsonScript.textContent) {
      return { isValid: false, variant: null }
    }

    try {
      const availableVariants = JSON.parse(jsonScript.textContent).variants

      // Check if the selected combination exists in available variants
      const isValidCombination = availableVariants.some((/** @type {any} */ variant) => {
        const variantOptions = variant.options || []
        return selectedOptionsValues.every(selectedValue => variantOptions.map((/** @type {any} */ option) => Number(option.id)).includes(Number(selectedValue)))
      })

      // If the combination is not valid, find the best matching variant
      if (!isValidCombination) {
        // Get the newly selected option from the event target
        let newlySelectedOption = null

        if (event && event.target instanceof HTMLSelectElement) {
          newlySelectedOption = Array.from(event?.target?.options).find((option) => option?.value === event?.target?.value)?.dataset?.optionValueId || null
        } else if (event && event.target instanceof HTMLElement) {
          newlySelectedOption = event.target.dataset?.optionValueId || null
        }

        return { isValid: false, variant: this.findBestMatchingVariant(availableVariants, selectedOptionsValues, newlySelectedOption) }
      }

      return { isValid: true, variant: null }
    } catch (error) {
      console.error('Debug: Error parsing available variants data:', error)
      return { isValid: false, variant: null }
    }
  }


  /**
   * Finds the best matching variant based on the number of shared options.
   * @param {any[]} availableVariants - Array of available variants.
   * @param {string[]} selectedOptionsValues - The currently selected option values.
   * @param {string | null} [newlySelectedOption] - The option that was just changed.
   */
  findBestMatchingVariant(availableVariants, selectedOptionsValues, newlySelectedOption = null) {
    const selectedOptionIds = selectedOptionsValues.map(value => Number(value))
    const newlySelectedOptionId = newlySelectedOption ? Number(newlySelectedOption) : null

    /** @type {any} */
    let bestMatch = null
    let maxSharedOptions = 0

    availableVariants.forEach((/** @type {any} */ variant) => {
      const variantOptionIds = variant.options?.map((/** @type {any} */ option) => Number(option.id)) || []

      // First priority: variants that include the newly selected option
      if (newlySelectedOptionId && !variantOptionIds.includes(newlySelectedOptionId)) {
        return
      }

      // Count how many options are shared between selected and this variant
      const sharedOptions = selectedOptionIds.filter(selectedId =>
        variantOptionIds.includes(selectedId)
      ).length

      if (sharedOptions > maxSharedOptions) {
        maxSharedOptions = sharedOptions
        bestMatch = variant
      }
    })

    return bestMatch
  }

  /**
   * Updates the wishlist variant.
   * @param {string | null | undefined} variantId - The active variant ID.
   * @returns {void}
   */
  updateWishlistVariant(variantId) {
    document.querySelectorAll('.add-to-wishlist-button').forEach((button) => {
      button.setAttribute('data-active', 'false')
    })

    if (!variantId) return

    const wishlistVariant = document.querySelector(`[data-wishlist-variant-id="${variantId}"]`)
    if (!wishlistVariant) return
    wishlistVariant.setAttribute('data-active', 'true')
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`)

      if (!targetElement) throw new Error('Target element not found')

      target = targetElement
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue)

      if (!newSelectedOption) throw new Error('Option not found')

      for (const option of target.options) {
        option.removeAttribute('selected')
      }

      newSelectedOption.setAttribute('selected', 'selected')
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {any} [bestMatchingVariant] - The best matching variant object when current selection is invalid.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, bestMatchingVariant = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl
    this.#pendingRequestUrl = productUrl
    const params = []

    // Use best matching variant option values if provided and current selection is invalid
    let optionValuesToUse = []
    if (bestMatchingVariant && !bestMatchingVariant.isValid && bestMatchingVariant.variant.options.length > 0) {
      optionValuesToUse = bestMatchingVariant.variant.options.map((/** @type {any} */ option) => option.id.toString())
    } else {
      optionValuesToUse = this.selectedOptionsValues
    }

    if (optionValuesToUse.length > 0) {
      params.push(`option_values=${optionValuesToUse.join(',')}`)
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0]
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`
    }
    return `${productUrl}?${params.join('&')}`
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  async fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort()
    this.#abortController = new AbortController()

    try {
      const response = await fetch(requestUrl, { signal: this.#abortController.signal })

      const textResponse = await response.text()
      this.#pendingRequestUrl = undefined
      const html = new DOMParser().parseFromString(textResponse, 'text/html')

      // Defer is only useful for the initial rendering of the page. Remove it here.
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer')

      const textContent = html.querySelector(`variant-picker #selected-variant`)?.textContent
      if (!textContent) return

      if (shouldMorphMain) {
        this.updateMain(html)
      } else {
        const newProduct = this.updateVariantPicker(html)

        // We grab the variant object from the response and dispatch an event with it.
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          )
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted by user')
      } else {
        console.error(error)
      }
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase())

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found')
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId
      const newProductUrl = newVariantPickerSource.dataset.productUrl

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl }
      }

      this.dataset.productId = newProductId
      this.dataset.productUrl = newProductUrl
    }

    morph(this, newVariantPickerSource)

    return newProduct
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main')
    const newMain = newHtml.querySelector('main')

    if (!main || !newMain) {
      throw new Error('No new main source found')
    }

    morph(main, newMain)
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked')

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined
    }

    return selectedOption
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this
    if (!selectedOption) return undefined
    const { optionValueId } = selectedOption.dataset

    if (!optionValueId) {
      throw new Error('No option value ID found')
    }

    return optionValueId
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset

      if (!optionValueId) throw new Error('No option value ID found')

      return optionValueId
    })
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker)
}
